import { authApi } from "@api";
import { enumFilter, initPage } from "@configs";
import { IAdmin, IAdminSearch, IPagination } from "@interfaces";
import { setLoading } from "@redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getApiErrorMessage } from "../apiError";
import { useNotify } from "../notifyHook";

export const adminQueryKeys = {
  all: ["admins"] as const,
  lists: () => [...adminQueryKeys.all, "list"] as const,
  list: (params: IAdminSearch) => [...adminQueryKeys.lists(), params] as const,
  details: () => [...adminQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...adminQueryKeys.details(), id] as const,
};

const fetchAdmins = async (params: IAdminSearch) => {
  const res = await authApi.admins(params);
  return {
    data: res.data?.data as IAdmin[],
    total: res.data?.nTotal as number,
  };
};

export const useAdminDetail = (id?: string) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { error } = useNotify();

  const query = useQuery({
    queryKey: adminQueryKeys.detail(id ?? ""),
    queryFn: async () => {
      const res = await authApi.detail(id as string);
      return res.data as IAdmin;
    },
    enabled: Boolean(id),
  });

  useEffect(() => {
    dispatch(setLoading(query.isFetching));
  }, [dispatch, query.isFetching]);

  useEffect(() => {
    if (query.isError) {
      error(getApiErrorMessage(query.error, t("message.fetch.fail")));
    }
  }, [query.isError, query.error, error, t]);

  return query;
};

export const useAdminListPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { error } = useNotify();

  const [page, setPage] = useState<IPagination>(initPage);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState("");
  const [date, setDate] = useState<string[]>([]);

  const params: IAdminSearch = {
    page: page.page,
    limit: page.limit,
    searchColumn: filters,
    search: text,
    startDate: date[0],
    endDate: date[1],
  };

  const query = useQuery({
    queryKey: adminQueryKeys.list(params),
    queryFn: () => fetchAdmins(params),
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    dispatch(setLoading(query.isFetching));
  }, [dispatch, query.isFetching]);

  useEffect(() => {
    if (query.isError) {
      error(getApiErrorMessage(query.error, t("message.fetch.fail")));
    }
  }, [query.isError, query.error, error, t]);

  const handleDateFilter = (_dates: null | (Dayjs | null)[], dateStrings: string[]) => {
    setDate(dateStrings);
  };

  const handlePagination = (current: number, size: number) => {
    setPage({ ...page, page: current, limit: size });
  };

  const changeFilter = (value: string) => setFilters(value);
  const searchText = (value: string) => setText(value);

  const handleClearSearch = () => {
    setText("");
    setFilters("");
  };

  const handleClearDate = () => setDate([]);

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    page,
    filters,
    text,
    date,
    refetch: query.refetch,
    handleDateFilter,
    handlePagination,
    changeFilter,
    searchText,
    handleClearSearch,
    handleClearDate,
  };
};

export const useCreateAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IAdmin) => authApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.lists() });
    },
  });
};

export const useUpdateAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payload, id }: { payload: IAdmin; id: string }) =>
      authApi.update(payload, id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.detail(id) });
    },
  });
};
